import { useCallback, useEffect, useMemo, useState } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { Link } from "react-router-dom";
import Chart from "react-apexcharts";

import api from "../../services/api";
import { useClient } from "../../hook/useClient";
import { notify } from "../../components/ui/Toast";
import { readApiError } from "../../utils/apiError";
import { describeEvent } from "../../utils/auditText";
import { exactTime, relativeTime } from "../../utils/time";

const MONTHS = ["yan", "fev", "mar", "apr", "may", "iyn", "iyl", "avg", "sen", "okt", "noy", "dek"];
const MONTHS_LONG = [
  "yanvar", "fevral", "mart", "aprel", "may", "iyun",
  "iyul", "avgust", "sentabr", "oktabr", "noyabr", "dekabr",
];

// "2026-10-22" is already a Tashkent day (the server buckets it); parsed by
// hand so the browser's own zone cannot move it by one.
const dayParts = (iso) => {
  const [, m, d] = iso.split("-").map(Number);
  return { d, m: m - 1 };
};

const SERIES = {
  messages: { label: "Xabarlar", unit: "ta xabar" },
  threads: { label: "Yangi muhokamalar", unit: "ta yangi muhokama" },
};

const nf = new Intl.NumberFormat("uz-UZ");
const n = (v) => nf.format(v ?? 0);

const MODE = {
  qr: { label: "QR (telefon)", icon: "tabler:qrcode" },
  ticket: { label: "Sayt logini (ticket)", icon: "tabler:ticket" },
};

/** "+12%" against last week -- or just this week's number when there is no base. */
const Delta = ({ now, before }) => {
  if (!before) return <span>{now ? `bu hafta +${n(now)}` : "bu hafta yoʻq"}</span>;
  const pct = Math.round(((now - before) / before) * 100);
  return (
    <>
      <span className="ms-delta">
        <Icon icon={pct >= 0 ? "tabler:arrow-up-right" : "tabler:arrow-down-right"} />
        {Math.abs(pct)}%
      </span>
      <span>
        bu hafta {n(now)}, oldin {n(before)}
      </span>
    </>
  );
};

/**
 * The cabinet's home screen (rebuilt 2026-10-22).
 *
 * Read top to bottom: which system this is and how people get in; how much is
 * happening, against last week; 30 days of activity; what is broken and how
 * to fix it; the busiest discussions; and what changed lately, by whom
 * (MySuhbat staff appear as "MySuhbat xodimi").
 *
 * For someone who may manage the client there is one more block: people the
 * site's ticket login turned away this week because they are not members,
 * each with a button to make them one. That is the most common support call
 * a ticket client has ("I cannot open the discussion"), answered in a click.
 *
 * One call: GET /clients/<id>/overview/, scoped by the server.
 */
const OverviewPage = () => {
  const { clientId } = useClient();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [series, setSeries] = useState("messages");
  const [adding, setAdding] = useState("");

  const load = useCallback(() => {
    return api
      .get(`/clients/${clientId}/overview/`)
      .then((res) => setData(res.data))
      .catch(() => setError("Maʼlumotlarni yuklab boʻlmadi."));
  }, [clientId]);

  useEffect(() => {
    load();
  }, [load]);

  const admit = async (person) => {
    setAdding(person.pinfl);
    try {
      await api.post(`/clients/${clientId}/members/`, { pinfl: person.pinfl });
      notify.success(
        `${person.name || person.pinfl} ishtirokchi qilindi — endi muhokamalarga kira oladi.`,
      );
      await load();
    } catch (err) {
      notify.error(readApiError(err, {}, "Ishtirokchi qilib boʻlmadi."));
    } finally {
      setAdding("");
    }
  };

  const chart = useMemo(() => {
    if (!data) return null;
    const days = data.daily;
    const unit = SERIES[series].unit;
    return {
      series: [{ name: SERIES[series].label, data: days.map((d) => d[series]) }],
      options: {
        chart: {
          type: "bar",
          toolbar: { show: false },
          zoom: { enabled: false },
          fontFamily: "Golos Text, system-ui, sans-serif",
          animations: { enabled: false },
        },
        colors: ["#0f766e"],
        plotOptions: { bar: { columnWidth: "62%", borderRadius: 4, borderRadiusApplication: "end" } },
        dataLabels: { enabled: false },
        responsive: [{ breakpoint: 640, options: { xaxis: { tickAmount: 4 } } }],
        states: { hover: { filter: { type: "darken", value: 0.85 } } },
        grid: {
          borderColor: "#eef1f6",
          xaxis: { lines: { show: false } },
          padding: { left: 6, right: 6 },
        },
        xaxis: {
          categories: days.map((d) => d.date),
          tickAmount: 10,
          labels: {
            rotate: 0,
            hideOverlappingLabels: true,
            style: { colors: "#667085", fontSize: "11px" },
            formatter: (iso) => {
              if (!iso) return "";
              const { d, m } = dayParts(iso);
              return `${d}-${MONTHS[m]}`;
            },
          },
          axisBorder: { color: "#e6e9ef" },
          axisTicks: { show: false },
          tooltip: { enabled: false },
        },
        yaxis: {
          min: 0,
          forceNiceScale: true,
          labels: {
            style: { colors: "#667085", fontSize: "11px" },
            formatter: (v) => (Number.isInteger(v) ? n(v) : ""),
          },
        },
        tooltip: {
          x: {
            formatter: (_, { dataPointIndex }) => {
              const iso = days[dataPointIndex]?.date;
              if (!iso) return "";
              const { d, m } = dayParts(iso);
              return `${d}-${MONTHS_LONG[m]}`;
            },
          },
          y: { formatter: (v) => `${n(v)} ${unit}`, title: { formatter: () => "" } },
          marker: { show: false },
        },
      },
    };
  }, [data, series]);

  if (error) {
    return (
      <div className="ms-panel">
        <div className="ms-state">
          <Icon icon="tabler:alert-triangle" />
          <span>{error}</span>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="ms-panel">
        <div className="ms-state">
          <div className="ms-spinner" />
        </div>
      </div>
    );
  }

  const { client, totals, week } = data;
  const ticket = client.auth_mode === "ticket";
  const mode = MODE[client.auth_mode] || MODE.qr;
  const total30 = data.daily.reduce((s, d) => s + d[series], 0);
  const topMax = Math.max(1, ...data.top_threads.map((t) => t.messages));

  const attention = data.attention.map((a) => {
    switch (a.kind) {
      case "no_origin":
        return {
          icon: "tabler:world-off",
          text: "Ruxsat etilgan domen yoʻq",
          sub: "vidjet hech qaysi saytda koʻrinmaydi",
          to: "/domenlar",
        };
      case "no_key":
        return {
          icon: "tabler:key-off",
          text: "Faol API kaliti yoʻq",
          sub: "tizimingiz muhokama ocha olmaydi — MySuhbat jamoasiga murojaat qiling",
        };
      case "no_member":
        return {
          icon: "tabler:users-minus",
          text: "Ishtirokchi yoʻq",
          sub: "muhokamalarni hech kim oʻqiy olmaydi",
          to: "/ishtirokchilar",
        };
      case "refused_logins":
        return {
          icon: "tabler:login-2",
          text: `${n(a.people)} kishi kira olmadi`,
          sub: `oxirgi 7 kunda ${n(a.attempts)} ta urinish — ishtirokchi emas`,
          anchor: data.can_manage ? "#rad-etilganlar" : null,
        };
      default:
        return { icon: "tabler:point", text: a.kind };
    }
  });

  return (
    <>
      <section className="ms-hero ms-hero--client">
        <div className="ms-hero-avatar">
          <Icon icon="tabler:building" />
        </div>
        <div className="ms-hero-who">
          <h1>{client.name}</h1>
          <div className="ms-hero-chips">
            <span className="ms-hero-chip">
              <Icon icon={mode.icon} />
              Kirish: {mode.label}
            </span>
            <span className="ms-hero-chip">
              <Icon icon={client.is_active ? "tabler:circle-check" : "tabler:plug-off"} />
              {client.is_active ? "Faol" : "Oʻchirilgan"}
            </span>
            <span className="ms-hero-chip">
              <Icon icon={data.can_manage ? "tabler:shield-check" : "tabler:eye"} />
              {data.can_manage ? "Siz boshqaruvchisiz" : "Siz faqat koʻrasiz"}
            </span>
          </div>
        </div>
        <div className="ms-hero-actions">
          <Link to="/muhokamalar" className="ms-btn ms-btn--hero">
            <Icon icon="tabler:messages" />
            Muhokamalar
          </Link>
        </div>
      </section>

      <div className="ms-dash-kpis">
        <Link className="ms-dkpi" to="/muhokamalar">
          <div className="ms-dkpi-top">
            <span>Muhokamalar</span>
            <Icon icon="tabler:messages" />
          </div>
          <div className="ms-dkpi-value">{n(totals.threads)}</div>
          <div className="ms-dkpi-foot">
            <Delta {...week.threads} />
          </div>
        </Link>
        <Link className="ms-dkpi" to="/muhokamalar">
          <div className="ms-dkpi-top">
            <span>Xabarlar</span>
            <Icon icon="tabler:message-2" />
          </div>
          <div className="ms-dkpi-value">{n(totals.messages)}</div>
          <div className="ms-dkpi-foot">
            <Delta {...week.messages} />
          </div>
        </Link>
        <Link className="ms-dkpi" to="/ishtirokchilar">
          <div className="ms-dkpi-top">
            <span>Ishtirokchilar</span>
            <Icon icon="tabler:users" />
          </div>
          <div className="ms-dkpi-value">{n(totals.members)}</div>
          <div className="ms-dkpi-foot">
            <span>bu hafta qoʻshilgan: {n(week.members.now)}</span>
          </div>
        </Link>
        <Link className="ms-dkpi" to="/domenlar">
          <div className="ms-dkpi-top">
            <span>Domenlar</span>
            <Icon icon="tabler:world" />
          </div>
          <div className="ms-dkpi-value">{n(totals.origins)}</div>
          <div className="ms-dkpi-foot">
            <span>{totals.origins ? "vidjet shu saytlarda ochiladi" : "hali qoʻshilmagan"}</span>
          </div>
        </Link>
      </div>

      <div className="ms-dash-row">
        <div className="ms-panel">
          <div className="ms-panel-head">
            <Icon icon="tabler:chart-bar" />
            <h2>Faollik — oxirgi 30 kun</h2>
            <span className="ms-td-muted" style={{ fontSize: 12.5 }}>
              jami {n(total30)} {SERIES[series].unit}
            </span>
            <div className="ms-toolbar-spacer" />
            <div className="ms-segmented" role="group" aria-label="Koʻrsatkich">
              {Object.entries(SERIES).map(([key, s]) => (
                <button
                  key={key}
                  type="button"
                  className={series === key ? "is-active" : ""}
                  aria-pressed={series === key}
                  onClick={() => setSeries(key)}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>
          <div className="ms-chart-wrap">
            <Chart type="bar" height={268} series={chart.series} options={chart.options} />
          </div>
        </div>

        <div className="ms-panel">
          <div className="ms-panel-head">
            <Icon icon="tabler:alert-circle" />
            <h2>Diqqat talab qiladi</h2>
            {attention.length > 0 && <span className="ms-tab-count is-danger">{attention.length}</span>}
          </div>
          {attention.length === 0 ? (
            <div className="ms-attn-ok">
              <Icon icon="tabler:circle-check" />
              <span>Hammasi joyida — tizimingiz toʻliq sozlangan.</span>
            </div>
          ) : (
            <ul className="ms-attn">
              {attention.map((a) => {
                const body = (
                  <>
                    <Icon icon={a.icon} />
                    <span>
                      {a.text}
                      <small>{a.sub}</small>
                    </span>
                  </>
                );
                return (
                  <li key={a.text}>
                    {a.to ? (
                      <Link to={a.to}>{body}</Link>
                    ) : a.anchor ? (
                      <a href={a.anchor}>{body}</a>
                    ) : (
                      <div>{body}</div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>

      {data.refused.length > 0 && (
        <div className="ms-panel" id="rad-etilganlar" style={{ marginBottom: 14 }}>
          <div className="ms-panel-head">
            <Icon icon="tabler:door-off" />
            <h2>Kira olmaganlar</h2>
            <span className="ms-td-muted" style={{ fontSize: 12.5 }}>
              oxirgi 7 kun — saytingizga kirgan, lekin ishtirokchi boʻlmagani uchun muhokamaga
              kiritilmagan
            </span>
          </div>
          <div className="ms-table-wrap">
            <table className="ms-table ms-table--compact">
              <thead>
                <tr>
                  <th>Shaxs</th>
                  <th style={{ width: 110 }}>Urinishlar</th>
                  <th style={{ width: 150 }}>Oxirgi urinish</th>
                  <th className="ms-td-actions" style={{ width: 190 }} />
                </tr>
              </thead>
              <tbody>
                {data.refused.map((p) => (
                  <tr key={p.pinfl}>
                    <td>
                      <div className="ms-namecell">
                        <b>{p.name || "Ism nomaʼlum"}</b>
                        <span>{p.pinfl}</span>
                      </div>
                      {p.was_removed && (
                        <span className="ms-badge ms-badge--progress" style={{ marginTop: 4 }}>
                          avval olib tashlangan
                        </span>
                      )}
                    </td>
                    <td className="ms-td-num">{n(p.attempts)}</td>
                    <td className="ms-td-muted" title={exactTime(p.last_at)}>
                      {relativeTime(p.last_at)}
                    </td>
                    <td className="ms-td-actions">
                      <button
                        type="button"
                        className="ms-btn ms-btn--primary ms-btn--sm"
                        disabled={adding === p.pinfl}
                        onClick={() => admit(p)}
                      >
                        <Icon icon="tabler:user-plus" />
                        {adding === p.pinfl ? "Qoʻshilmoqda..." : "Ishtirokchi qilish"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="ms-dash-row ms-dash-row--half">
        <div className="ms-panel">
          <div className="ms-panel-head">
            <Icon icon="tabler:flame" />
            <h2>Eng faol muhokamalar</h2>
            <span className="ms-td-muted" style={{ fontSize: 12.5 }}>
              xabarlar, oxirgi 30 kun
            </span>
          </div>
          {data.top_threads.length === 0 ? (
            <div className="ms-state">
              <Icon icon="tabler:message-off" />
              <span>Oxirgi 30 kunda xabar yozilmagan.</span>
            </div>
          ) : (
            <div className="ms-table-wrap">
              <table className="ms-table ms-table--compact">
                <thead>
                  <tr>
                    <th>Muhokama</th>
                    <th style={{ width: "34%" }}>Xabarlar</th>
                    <th style={{ width: 120 }}>Oxirgi xabar</th>
                  </tr>
                </thead>
                <tbody>
                  {data.top_threads.map((t) => (
                    <tr key={t.id}>
                      <td className="ms-td-clamp">
                        <Link className="ms-sublink" to={`/muhokamalar/${t.id}`} title={t.title}>
                          <b>{t.title}</b>
                        </Link>
                      </td>
                      <td>
                        <div className="ms-dmeter" title={`${n(t.messages)} ta xabar`}>
                          <span>
                            <i style={{ width: `${(t.messages / topMax) * 100}%` }} />
                          </span>
                          <b>{n(t.messages)}</b>
                        </div>
                      </td>
                      <td className="ms-td-muted" title={exactTime(t.last_message_at)}>
                        {t.last_message_at ? relativeTime(t.last_message_at) : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="ms-panel">
          <div className="ms-panel-head">
            <Icon icon="tabler:history" />
            <h2>Soʻnggi oʻzgarishlar</h2>
            <span className="ms-td-muted" style={{ fontSize: 12.5 }}>
              ishtirokchilar, domenlar, guruhlar
            </span>
          </div>
          {data.changes.length === 0 ? (
            <div className="ms-state">
              <Icon icon="tabler:history-off" />
              <span>Hali oʻzgarish yozilmagan.</span>
            </div>
          ) : (
            <ul className="ms-timeline">
              {data.changes.map((e) => {
                const { icon, text, to } = describeEvent(e);
                return (
                  <li key={e.id}>
                    <span className="ms-timeline-dot">
                      <Icon icon={icon} />
                    </span>
                    <span className="ms-timeline-body">
                      {to ? (
                        <Link className="ms-sublink" to={to}>
                          {text}
                        </Link>
                      ) : (
                        text
                      )}
                      <small>
                        {e.actor || "—"} · {relativeTime(e.created_at)}
                      </small>
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>

      {/* Who may take part -- worded for how THIS client lets people in. The
          old text said a site login is never enough, which stopped being true
          for ticket clients on 2026-10-21. */}
      <div className="ms-note" style={{ marginTop: 0 }}>
        <span>Kim qatnasha oladi</span>
        <p>
          {ticket
            ? "Saytingizga kirgan odam muhokamani oʻz login maʼlumotlari bilan ochadi — QR kod kerak emas. Birinchi marta kirgan yangi odam avtomatik ishtirokchi boʻladi; siz olib tashlagan odam esa qayta qoʻshilmaguncha kira olmaydi."
            : "Muhokamalarni faqat siz qoʻshgan ishtirokchilar oʻqiy va yoza oladi. Ular kim ekanini MySuhbat ilovasida QR kod orqali tasdiqlaydi — saytingizga kirgan boʻlish yetarli emas."}
        </p>
      </div>
    </>
  );
};

export default OverviewPage;
